import { parseISO } from 'date-fns';
import { isEqual } from 'lodash';
import moment from 'moment';

import i18n from 'shared/lib/i18n';

import {
  filterObjectByKeyValue,
  formatDate,
  replaceUnderscoreWithSpace,
  sortByKey,
} from '../../utils';
import { formatSelect } from '../Forms/utils';
import { getFullName } from '../Person/utils';
import {
  generatePossibleHours,
  generatePossibleMinutes,
  getInitialSchedule,
} from '../Schedule/utils';

import {
  DEFAULT_END_ANY_TIME,
  DEFAULT_SCHEDULE,
  DEFAULT_START_TIME,
} from '../Schedule/constants';
import { DATE_TIME_FORMAT, TIME_REGEX } from './constants';

// navigation utils
export const getAmenityBaseUrl = (url) =>
  `${url?.substring(0, url?.indexOf('/amenity'))}/amenity`;

// filter utils
export const getBuildingFilterOptions = (buildings, pmcId, buildingId) => {
  const filteredBuildings = pmcId
    ? filterObjectByKeyValue(buildings, 'parent.id', +pmcId)
    : buildingId
    ? filterObjectByKeyValue(buildings, 'id', +buildingId)
    : buildings;
  return formatSelect(Object.values(filteredBuildings)).sort((a, b) =>
    sortByKey(a, b, 'label'),
  );
};

export const getAmenityFilterOptions = (amenities) =>
  amenities?.length > 0
    ? formatSelect(amenities?.filter(({ active }) => active)).sort((a, b) =>
        sortByKey(a, b, 'label'),
      )
    : [];

export const getPersonFilterOptions = (people) =>
  formatSelect(
    people?.map((person) => ({ id: person?.id, name: getFullName(person) })),
  ).sort((a, b) => sortByKey(a, b, 'label'));

// date/time utils
export const getMomentDateAndTime = (date) =>
  moment(date).locale('en').format(DATE_TIME_FORMAT).split(' ');

export const onDateChangeFormatter = (rawDate, prevDate) => {
  const date = moment(rawDate).minute(0).second(0);
  if (!prevDate) return date;

  const [newDate] = getMomentDateAndTime(date);
  const [, oldTime] = getMomentDateAndTime(prevDate);

  return moment(`${newDate} ${oldTime}`, DATE_TIME_FORMAT);
};

// TODO: is not used?
export const formatSelectedTime = (type, dateValue, time) =>
  type === 'hours'
    ? moment(dateValue).hour(time).minute(0).format(DATE_TIME_FORMAT)
    : type === 'minutes'
    ? moment(dateValue).minute(time).format(DATE_TIME_FORMAT)
    : null;

const getReservationTime = (type, date, isStartTime) =>
  type === 'hours'
    ? moment(date)
        .minute(isStartTime ? 0 : 59)
        .format(DATE_TIME_FORMAT)
    : date;

export const getIsAfterStartDate = (type, startDate, timeOnUI) => {
  const reservationStart = getReservationTime(type, startDate, true);
  return moment(timeOnUI).isSameOrAfter(reservationStart);
};

export const getIsBeforeEndDate = (type, endDate, selectedTime) => {
  const reservationEnd = getReservationTime(type, endDate, false);
  return moment(selectedTime).isSameOrBefore(reservationEnd);
};

export const parseLimitTime = (time) => time?.split(':');

export const formatTimeRange = (time, format = DATE_TIME_FORMAT) => {
  if (!time) return null;
  const [h, m, s] = parseLimitTime(time);
  return moment().set({ h, m, s }).format(format);
};

const addTime = (time, h, m, s) =>
  moment(time).add(h, 'h').add(m, 'm').add(s, 's').format(DATE_TIME_FORMAT);

const subtractTime = (time, h, m) =>
  moment(time).subtract(h, 'h').subtract(m, 'm');

const getMinEndTime = (startDate, selectedAmenity) => {
  const [h, m, s] = parseLimitTime(
    selectedAmenity?.data?.minimumReservationTime,
  );
  return addTime(startDate, h, m, s);
};

const getMaxEndTime = (startDate, selectedAmenity) => {
  const [h, m, s] = parseLimitTime(
    selectedAmenity?.data?.maximumReservationTime === '00:00:00'
      ? '23:59:59'
      : selectedAmenity?.data?.maximumReservationTime,
  );
  return addTime(startDate, h, m, s);
};

const getMaxSafeStartTime = (maxEndTime, selectedAmenity) => {
  const [h, m, s] = parseLimitTime(
    selectedAmenity?.data?.minimumReservationTime,
  );
  return subtractTime(maxEndTime, h, m, s);
};

export const getIsSafeStartTime = (
  timeOnUI,
  dateValue,
  selectedAmenity,
  type,
) => {
  const [selectedDate] = getMomentDateAndTime(dateValue);
  if (selectedAmenity?.allowedHours?.length === 0) return true;
  if (!selectedAmenity?.allowedHours?.length) return false;

  return !selectedAmenity?.allowedHours?.find(({ endTime }) => {
    let maxEndTime = moment(`${selectedDate}T${endTime}`);
    if (endTime === DEFAULT_END_ANY_TIME) {
      maxEndTime = moment(maxEndTime).add(1, 'second');
    }
    const maxSafeStartTime = getMaxSafeStartTime(maxEndTime, selectedAmenity);
    const constraints = type === 'minute' ? '(]' : '()';
    return moment(timeOnUI).isBetween(
      maxSafeStartTime,
      maxEndTime,
      type,
      constraints,
    );
  });
};

export const getIsAvailableAfterMidnight = (startDate, selectedAmenity) => {
  if (!selectedAmenity?.data) return null;
  const maxEndTime = getMaxEndTime(startDate, selectedAmenity);
  return moment(maxEndTime).isAfter(moment(startDate).format(), 'day');
};

export const getIsInMinMaxTimeRange = (
  type, // selected time type - "hour" or "minute"
  startDate, // selected "startDate" in the picker
  selectedAmenity, // reservation is being created for this amenity
  timeOnUI,
) => {
  if (!type || !startDate || !selectedAmenity?.data || !timeOnUI) return null;

  const reservationStart = getReservationTime(type, startDate, true);
  const minEndTime = getMinEndTime(reservationStart, selectedAmenity);
  const maxEndTime = getMaxEndTime(reservationStart, selectedAmenity);
  return moment(timeOnUI).isBetween(minEndTime, maxEndTime, type, '[]');
};

export const getIsAnyTime = (startTime, endTime) => {
  if (!startTime || !endTime) return false;
  return (
    startTime.slice(0, 8) === DEFAULT_START_TIME &&
    endTime.slice(0, 8) === DEFAULT_END_ANY_TIME
  );
};

export const getIsEveryDaySelected = (schedule) =>
  schedule
    ? !Object.values(schedule)
        ?.flat()
        ?.find(
          ({ startTime, endTime }) =>
            startTime !== DEFAULT_START_TIME ||
            endTime !== DEFAULT_END_ANY_TIME,
        )
    : null;

export const getAmenityReservationTime = (
  date,
  startTime,
  endTime,
  amenity,
) => {
  const startDate = date.replace(TIME_REGEX, startTime);
  const endDate = date.replace(TIME_REGEX, endTime);

  const isSameDay = moment(startDate).isSame(endDate, 'day');

  const timezone = amenity?.timezone;
  const start = formatDate(startDate, true, 'MM/DD, hh:mma');
  const end = formatDate(endDate, true, 'hh:mma');
  return { displayDate: `${start} - ${end} ${timezone}`, isSameDay };
};

export const formatAmenityReservationTime = (time) => {
  const hours = formatTimeRange(time, 'H');
  const minutes = formatTimeRange(time, 'm');
  if (hours === '0' && minutes === '0') return i18n.t('title.noLimit');
  let displayTime = '';
  if (hours !== '0') {
    displayTime += `${hours} ${i18n.t('time.hours')}`;
  }
  if (minutes !== '0') {
    displayTime += ` ${minutes} ${i18n.t('time.minutes')}`;
  }
  return displayTime;
};

export const convertDataToMinutes = (time = '') => {
  // format value from HH:MM:SS to minutes
  const seconds = time.split(':').reduce((acc, time) => 60 * acc + +time);
  const minutes = Math.floor(seconds / 60);

  return minutes;
};

// initial values utils
export const getReservationInitialValues = (reservation) => {
  if (!reservation) return {};
  const {
    amenity,
    contactPerson: person,
    date,
    startTime,
    endTime,
    comments,
  } = reservation;
  const building = amenity?.building?.id;
  const id = amenity?.id;
  const contactPerson = person?.id;
  const startDate = moment(`${date?.substring(0, 10)}T${startTime}`);
  const endDate = moment(`${date?.substring(0, 10)}T${endTime}`);
  const reservationLength =
    convertDataToMinutes(endTime) - convertDataToMinutes(startTime);
  return {
    building,
    id,
    contactPerson,
    startDate,
    endDate,
    comments,
    startTime,
    endTime,
    reservationLength,
  };
};

export const getAmenityDefaultInitialValues = (buildingId) => ({
  building: buildingId,
  minimumDaysBeforeReservation: 2,
  maximumDaysBeforeReservation: 90,
  schedule: DEFAULT_SCHEDULE,
  active: true,
  minimumReservationTime: formatTimeRange('00:15:00'),
  maximumReservationTime: formatTimeRange('03:00:00'),
  simultaneousNumberOfReservations: 1,
  rentalAgreementRequired: false,
  requiresApproval: false,
  forceAmenitySchedule: false,
  connectedDoors: [],
});

export const formatAmenityInitialDoors = (doors) =>
  Array.isArray(doors) ? doors?.map(({ id }) => id) : [];

export const getAmenityInitialValues = (amenityData) => {
  if (!amenityData?.id) return {};
  const {
    building: { id: building },
    createdAt,
    doors,
    unit,
    reservationsCount,
    reservationsLeft,
    maximumReservationTime: maxTime,
    minimumReservationTime: minTime,
    reservationAgreements,
    reservationApprovals,
    schedule: rawSchedule,
    ...rest
  } = amenityData;

  return {
    building,
    schedule: getInitialSchedule(rawSchedule),
    maximumReservationTime: formatTimeRange(maxTime),
    minimumReservationTime: formatTimeRange(minTime),
    reservationAgreements: false,
    reservationApprovals: false,
    ...rest,
  };
};

export const getBuildingTimezone = (common, buildings, buildingId) => {
  const selectedBuildingTimezone =
    buildings?.[buildingId]?.buildingData?.timezone;
  const timezoneName =
    common?.timezones?.length &&
    common?.timezones?.find(({ id }) => id === selectedBuildingTimezone)?.name;
  return timezoneName && replaceUnderscoreWithSpace(timezoneName);
};

// schedule utils
export const getAmenitySchedule = (isAnyTime, timeSlots) => {
  const defaultTime = isAnyTime
    ? [{ startTime: DEFAULT_START_TIME, endTime: DEFAULT_END_ANY_TIME }]
    : timeSlots;
  return {
    monday: defaultTime,
    tuesday: defaultTime,
    wednesday: defaultTime,
    thursday: defaultTime,
    friday: defaultTime,
    saturday: defaultTime,
    sunday: defaultTime,
  };
};

const formatEverydayValues = (everyday) => {
  const { endTime, ...rest } = everyday;
  if (endTime === DEFAULT_START_TIME) {
    return { endTime: DEFAULT_END_ANY_TIME, ...rest };
  }
  return everyday;
};

const formatDaysValues = (days) => {
  const formattedDays = {};
  if (!days || typeof days !== 'object') return null;
  Object.keys(days).forEach((day) => {
    const dayTimeRanges = [];
    days[day]?.forEach(({ startTime, endTime }) => {
      if (!startTime || !endTime) return; // do not include empty timeslots
      let formattedEndTime = endTime;
      if (endTime === DEFAULT_START_TIME) {
        formattedEndTime = DEFAULT_END_ANY_TIME;
      }
      dayTimeRanges.push({ startTime, endTime: formattedEndTime });
    });
    formattedDays[day] = dayTimeRanges;
  });
  return formattedDays;
};

export const schedulePreSubmit = (schedule) => {
  const { everyday: rawEveryday, ...rawDays } = schedule;
  // check if a user has selected 12:00 am of the next day ->
  // replace with 23:59 pm (due to the one day limit and 15-min intervals)
  const everyday = rawEveryday && formatEverydayValues(rawEveryday);
  const days = formatDaysValues(rawDays);

  const { isOn: isEverydayOn, timeSlots } = everyday || {};
  if (isEverydayOn) {
    const isAnyTime = getIsAnyTime(
      timeSlots?.[0]?.startTime,
      timeSlots?.[0]?.endTime,
    );
    return getAmenitySchedule(isAnyTime, timeSlots);
  }
  return days;
};

// day validation
// 1. check if the `day` is reserved
// 2. check if the `day` is between [minimumDaysBeforeReservation and maximumDaysBeforeReservation]
// 3. days in the past are disabled (in the component)
export const isInvalidDate = (day, selectedAmenity) => {
  if (!day || !moment(day).isValid()) return true;

  const isDayReserved = !!selectedAmenity?.reservedDates?.find((date) =>
    moment(day).isSame(date, 'day'),
  );

  if (isDayReserved || !selectedAmenity?.data) return true;
  const { maximumDaysBeforeReservation, minimumDaysBeforeReservation } =
    selectedAmenity?.data || {};

  const daysFromNow = moment(day).diff(moment().startOf('day'), 'days');

  const isDayOut =
    daysFromNow < minimumDaysBeforeReservation ||
    daysFromNow >= maximumDaysBeforeReservation;

  if (isDayOut) return true;
  return false;
};

export const amenitiesReservationsDayAllowed = (value, values, props) => {
  const result = isInvalidDate(value, props?.selectedAmenity);
  return result ? i18n.t('errors.invalidDate') : null;
};

export const isTimeReserved = (timeOnUI, dateValue, selectedAmenity, type) => {
  const [selectedDate] = getMomentDateAndTime(dateValue);
  if (selectedAmenity?.allowedHours?.length === 0) return true;
  if (!selectedAmenity?.allowedHours?.length) return false;

  return !!selectedAmenity?.allowedHours?.find(({ startTime, endTime }) => {
    const reservedStartTime = moment(`${selectedDate}T${startTime}`)
      .second(0)
      .format(DATE_TIME_FORMAT);
    const reservedEndTime = moment(`${selectedDate}T${endTime}`)
      .second(0)
      .format(DATE_TIME_FORMAT);
    return !!moment(timeOnUI).isBetween(
      reservedStartTime,
      reservedEndTime,
      type,
      '[]',
    );
  });
};

// amenity reservations validation
export const getDisabledHours = (fieldName, startDate, selectedAmenity) => {
  const possibleHours = generatePossibleHours();
  let disabledHours = [];

  possibleHours.forEach((hour) => {
    const timeOnUI = moment(startDate).set({ h: hour, m: 59, s: 59 });

    // 1. Time should be in the future
    if (moment(timeOnUI).isBefore()) {
      disabledHours.push(hour);
    }

    // 2. Time shouldn't be reserved
    const isReserved = !isTimeReserved(
      timeOnUI,
      startDate,
      selectedAmenity,
      'hour',
    );
    if (isReserved) {
      disabledHours.push(hour);
    }

    if (fieldName === 'startDate') {
      // 3. Make sure there will be a valid `endDate`
      // take every `endTime` from the allowed hours and subtract `minReservationTime`
      const isSafeStartTime = getIsSafeStartTime(
        timeOnUI,
        startDate,
        selectedAmenity,
        'hour',
      );
      if (!isSafeStartTime) {
        disabledHours.push(hour);
      }
    }

    if (fieldName === 'endDate') {
      // 4. End time should be after start time (startDate)
      const isSameOrAfterStartDate = moment(timeOnUI).isSameOrAfter(startDate);
      if (!isSameOrAfterStartDate) {
        disabledHours.push(hour);
      }

      // 5. End time should meet the min/max time constraints
      const isInMinMaxTimeRange = getIsInMinMaxTimeRange(
        'hour',
        startDate,
        selectedAmenity,
        timeOnUI,
      );
      if (!isInMinMaxTimeRange) {
        disabledHours.push(hour);
      }
    }
  });

  if (getIsAvailableAfterMidnight(startDate, selectedAmenity)) {
    disabledHours = [...new Set(disabledHours)].filter((value) => value !== 0);
  }
  return [...new Set(disabledHours)];
};

export const getDisabledMinutes = (
  selectedHour,
  fieldName,
  startDate,
  selectedAmenity,
) => {
  const possibleMinutes = generatePossibleMinutes();
  let disabledMinutes = [];

  possibleMinutes.forEach((minute) => {
    const timeOnUI = moment(startDate).set({
      h: selectedHour,
      m: minute,
    });

    // 1. Time should be in the future
    if (moment(timeOnUI).isBefore()) {
      disabledMinutes.push(minute);
    }

    // 2. Time shouldn't be reserved
    const isReserved = !isTimeReserved(
      timeOnUI,
      startDate,
      selectedAmenity,
      'minute',
    );
    if (isReserved) {
      disabledMinutes.push(minute);
    }

    if (fieldName === 'startDate') {
      // 3. Make sure there will be a valid `endDate`
      // take every `endTime` from the allowed hours and subtract `minReservationTime`
      const isSafeStartTime = getIsSafeStartTime(
        timeOnUI,
        startDate,
        selectedAmenity,
        'minute',
      );
      if (!isSafeStartTime) {
        disabledMinutes.push(minute);
      }
    }

    if (fieldName === 'endDate') {
      // 4. End time should be after start time (startDate)
      const isAfterStartDate = moment(timeOnUI).isAfter(startDate);
      if (!isAfterStartDate) {
        disabledMinutes.push(minute);
      }

      // 5. End time should meet the min/max time constraints
      const isInMinMaxTimeRange = getIsInMinMaxTimeRange(
        'minute',
        startDate,
        selectedAmenity,
        timeOnUI,
      );
      if (!isInMinMaxTimeRange) {
        disabledMinutes.push(minute);
      }
    }
  });

  if (
    getIsAvailableAfterMidnight(startDate, selectedAmenity) &&
    selectedHour === 0
  ) {
    disabledMinutes = [...new Set(disabledMinutes)].filter(
      (value) => value !== 0,
    );
  }
  return [...new Set(disabledMinutes)];
};

export const amenitiesReservationsTimeAllowed = (
  value,
  values,
  props,
  fieldName,
) => {
  const hour = moment(value).hour();
  const minute = moment(value).minute();

  const invalidHours = getDisabledHours(
    fieldName,
    values?.startDate,
    props?.selectedAmenity,
  );
  const invalidMinutes = getDisabledMinutes(
    hour,
    fieldName,
    values?.startDate,
    props?.selectedAmenity,
  );
  const isInvalidHour = invalidHours?.find(
    (disabledHour) => disabledHour === hour,
  );
  const isInvalidMinute = invalidMinutes?.find(
    (disabledMinute) => disabledMinute === minute,
  );

  let error = null;
  if (isInvalidHour) {
    error = i18n.t('errors.invalidHour');
  }
  if (isInvalidMinute) {
    error = error
      ? i18n.t('errors.invalidHourAndMinute')
      : i18n.t('errors.invalidMinute');
  }
  return error;
};

export const amenitiesReservationsStartTimeAllowed = (value, values, props) =>
  amenitiesReservationsTimeAllowed(value, values, props, 'startDate');

export const amenitiesReservationsEndTimeAllowed = (value, values, props) =>
  amenitiesReservationsTimeAllowed(value, values, props, 'endDate');

export const minDaysToBookLimit = (value, { maximumDaysBeforeReservation }) => {
  if (!value) return null;
  if (value > 365) return i18n.t('errors.moreThanOneYear');
  if (
    maximumDaysBeforeReservation &&
    Number.parseInt(value, 10) >=
      Number.parseInt(maximumDaysBeforeReservation, 10)
  )
    return `${i18n.t('errors.cantBeEqualOrGreater')} "${i18n.t(
      'title.maximumDaysBeforeReservation',
    )}"`;
  return null;
};

export const maxDaysToBookLimit = (value, { minimumDaysBeforeReservation }) => {
  if (!value) return null;
  if (value > 366) return i18n.t('errors.moreThanOneYear');
  if (
    minimumDaysBeforeReservation &&
    Number.parseInt(value, 10) <=
      Number.parseInt(minimumDaysBeforeReservation, 10)
  )
    return `${i18n.t('errors.cantBeEqualOrLess')} "${i18n.t(
      'title.minimumDaysBeforeReservation',
    )}"`;
  return null;
};

export const simultaneousReservationsLimit = (value) => {
  if (!value) return null;
  if (value > 20 || value < 1)
    return `1 - 20 ${i18n.t('errors.numberReservationsAllowed')}`;
  return null;
};

export const formatTimeSlotsToMinutes = (time) => {
  // format value from 10:00am to minutes number (10:00am => 600)
  let result;
  const t = time.replace('am', '').replace('pm', '').split(':');
  const minutes = +t[0] * 60 + +t[1];

  const isAM = time.replace(/[^\p{L}]/gu, '') === 'am';
  const isPM = time.replace(/[^\p{L}]/gu, '') === 'pm';

  if (isAM && t[0] === '12') {
    // if it is 12am => should be 00:00 => then it means we need to do 12h - 12h = 00:00
    result = minutes - 720;
  } else if (isPM && t[0] !== '12') {
    // if it is pm and not equal to 12h => we need to add 12h === 720min because 1pm = 13, 2pm = 14
    result = minutes + 720;
  } else {
    // if the time between 01:00 and 12:00 (01am and 12pm) => we just return it without affecting
    result = minutes;
  }

  return result;
};

export const formatReservationLengthToMinutes = (length) => {
  // format value from 1h or 30min to minutes number (1h => 60)
  if (length.match(/[:]/gm)) {
    const t = length.replace('h', '').replace('min', '').split(':');
    const minutes = +t[0] * 60 + +t[1];
    return minutes;
  }
  const minutes = +length.replace('h', '').replace('min', '');
  return minutes;
};

export const minsToHHMMSS = (num) => {
  // format value from number to HH:MM:SS format (60 => 1:00:00)
  const mins_num = parseFloat(num, 10);
  let hours = Math.floor(mins_num / 60);
  let minutes = Math.floor(mins_num - (hours * 3600) / 60);
  let seconds = Math.floor(mins_num * 60 - hours * 3600 - minutes * 60);

  if (hours < 10) {
    hours = `0${hours}`;
  }
  if (minutes < 10) {
    minutes = `0${minutes}`;
  }
  if (seconds < 10) {
    seconds = `0${seconds}`;
  }
  return `${hours}:${minutes}:${seconds}`;
};

export const formatReservationsEvents = (items) => {
  if (!items || !items.length) return [];
  if (items?.[0]?.isPlaceholder) return items;
  return items.map((reservation) => {
    const { id, date, startTime, endTime, amenity } = reservation;
    const start = moment(`${date?.substring(0, 10)}T${startTime}`).format();
    const end = moment(`${date?.substring(0, 10)}T${endTime}`).format();
    const title = amenity?.name;
    return {
      id,
      start,
      end,
      title,
      reservation,
      className: 'default-amenity-reservation-event',
    };
  });
};

export const generateReservationsEvents = (items) => {
  if (!items || !items.length) return [];
  return items
    .filter((date) => date)
    .map((date) => ({
      id: date,
      start: new Date(date).toISOString(),
      end: new Date(date).toISOString(),
      title: '',
      isPlaceholder: true,
      className:
        'default-amenity-reservation-event past-amenity-reservation-event',
    }));
};

export const timeConvertToHrFormat = (num) => {
  // convert minutes to 1h or 30min or 1:30h format (90 => 1:30h or 60 => 1h or 30 => 30min)
  const hours = num / 60;
  const rHours = Math.floor(hours);
  const minutes = (hours - rHours) * 60;
  const rMinutes = Math.round(minutes);
  if (rMinutes && rHours) {
    return `${rHours}:${rMinutes}${i18n.t('time.h')}`;
  }
  if (!rHours) {
    return `${rMinutes}${i18n.t('time.min')}`;
  }
  if (!rMinutes) {
    return `${rHours}:00${i18n.t('time.h')}`;
  }
  return '';
};

export const timeConvertToAmPmFormat = (num) => {
  // convert time from minutes to 10:00am format (600 => 10:00am)
  const hours = num / 60;
  let rHours = Math.floor(hours);
  const minutes = (hours - rHours) * 60;
  let rMinutes = Math.round(minutes);
  const amPm = rHours >= 12 ? 'pm' : 'am';
  rHours %= 12;
  rHours = rHours || 12; // the hour '0' should be '12'
  rMinutes = rMinutes < 10 ? `0${rMinutes}` : rMinutes;
  const strTime = `${rHours}:${rMinutes}${amPm}`;
  return strTime;
};

export const generateReservationIntervals = (min = 0, max = 0) => {
  // generate reservation length time slots based on min and max reservation values
  const interval = 15;
  const slots = [];
  slots.push(min);

  if (min !== undefined && max) {
    while (slots[slots.length - 1] < max) {
      slots.push(slots[slots.length - 1] + interval);
    }
  }
  return slots;
};

export const generateAllowedReservationLength = (
  // generate reserved length time slots based on allowed hours
  min = 0,
  max = 0,
  intervalMin,
  intervalMax,
  startTime,
) => {
  const interval = 15;
  const slots = [];
  slots.push(interval);

  if (min !== undefined && max) {
    while (slots[slots.length - 1] < max) {
      slots.push(slots[slots.length - 1] + interval);
    }
  }

  const data = generateReservationIntervals(intervalMin, intervalMax).filter(
    (item) => item <= slots[slots.length - 1] - slots[0],
  );

  // to avoid non-allowed reservation time, we need to check, if start time  + reservation length < max (last minute of amenities schedule)
  let result = [];
  if (startTime) {
    result = data
      .filter((item) => item + formatTimeSlotsToMinutes(startTime) <= max)
      .map((slot) => timeConvertToHrFormat(slot));
  } else {
    result = data.map((slot) => timeConvertToHrFormat(slot));
  }

  return result;
};

export const collectTimeSlots = (min = 0, max = 0, minReservationTime = 0) => {
  // collect allowed hours to generate time slots
  const interval = 15;
  const slots = [];
  if (min === 0) {
    slots.push(minReservationTime);
  } else {
    slots.push(min);
  }

  // conditions to generate time slots, depends on minimum reservation time

  if (min !== undefined && max) {
    if (minReservationTime >= interval) {
      while (slots[slots.length - 1] < max - minReservationTime) {
        slots.push(slots[slots.length - 1] + interval);
      }
    } else if (minReservationTime === 0) {
      while (slots[slots.length - 1] < max - interval * 2) {
        slots.push(slots[slots.length - 1] + interval);
      }
    } else {
      while (slots[slots.length - 1] <= max - interval - 1) {
        slots.push(slots[slots.length - 1] + interval);
      }
    }
  }

  const result = slots
    .filter((item) => item !== max)
    .map((slot) => timeConvertToAmPmFormat(slot));
  return result;
};

export const generateStartTimeSlots = (allowedSlots = [], interval) => {
  // generate start time slots
  const result = allowedSlots
    .map((item) =>
      collectTimeSlots(
        convertDataToMinutes(item.startTime),
        convertDataToMinutes(item.endTime),
        interval,
      ),
    )
    .flat();

  return result;
};

export const generateReservationSlots = (
  allowedSlots = [],
  min,
  max,
  startTime,
) => {
  // generate reservation length slots
  const result = allowedSlots
    .map((item) =>
      generateAllowedReservationLength(
        convertDataToMinutes(item.startTime),
        convertDataToMinutes(item.endTime),
        min,
        max,
        startTime,
      ),
    )
    .flat();

  return [...new Set(result)];
};

// returns string format as Friday, 11/04, 1:30pm – 4:30pm
export const getValidDateFormat = (rowInfo) => {
  const parsedData = parseISO(rowInfo?.original?.date);
  const day = parsedData.toLocaleDateString('en-us', { weekday: 'long' });
  const { displayDate } = getAmenityReservationTime(
    rowInfo?.original.date?.slice(0, -6),
    rowInfo?.original.startTime,
    rowInfo?.original.endTime,
    rowInfo?.original.amenity,
  );

  return `${day} ${displayDate}`;
};

// returns phone_number or email
export const getContactInfo = (type, personInfo) =>
  personInfo?.contactInfo[1]?.type === type
    ? personInfo.contactInfo[1].contactInfo
    : personInfo?.contactInfo[0]?.type === type
    ? personInfo.contactInfo[0].contactInfo
    : '';

export const arePropsEqual = (prevProps, nextProps) =>
  isEqual(prevProps, nextProps);
